// @flow
import { DataGridPremium } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CustomPagination } from "../../../../components/mui/grids/dataGridCustom/customPagination";
import DataGridWrapper from "../../../../components/mui/grids/dataGridWrapper/dataGridWrapper";
import { muiGridLocales } from "../../../../helpers/mui/muiLocales";
import useAuditing from "../../../../hooks/auditing/useAuditing";
import useUserData from "../../../../hooks/user/useUserData";
import TruncateDescription from "../../../../pages/dashboard/partials/truncateDescription";
import { ISSUES_FORM } from "../../locales/namespaces";
import "./issueAuditingHistory.css";

type Props = {
	IssueId: string
};

const IssueAuditingHistory = (props: Props) => {
	const { IssueId } = props
	const { language, dateFormat, timeFormat } = useUserData()
	const { loading, entityHistory, getEntityHistory } = useAuditing({
		entity: 'sscIssues',
		entityId: IssueId,
		entityFields: [
			{ FieldName: 'Description', UserField: 0 },
			{ FieldName: 'AssigneeId', UserField: 1 },
			{ FieldName: 'StatusId', UserField: 0 },
			{ FieldName: 'TypeId', UserField: 0 },
			{ FieldName: 'PriorityId', UserField: 0 },
			{ FieldName: 'KindId', UserField: 0 },
			{ FieldName: 'ActualStartDt', UserField: 0 },
			{ FieldName: 'ActualEndDt', UserField: 0 },
			{ FieldName: 'TargetedStartDt', UserField: 0 },
			{ FieldName: 'TargetedStartTm', UserField: 0 },
			{ FieldName: 'TargetedEndDt', UserField: 0 },
			{ FieldName: 'TargetedEndTm', UserField: 0 },
			{ FieldName: 'ActualVisitDt', UserField: 0 },
			{ FieldName: 'ActualVisitTm', UserField: 0 }
		]
	})
	const { t } = useTranslation(ISSUES_FORM)
	
	const columns = useMemo(() => [
		
		{
			field: 'ModifiedAt',
			headerName: t('Modification Date'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
			}
		},
		{
			field: 'LastModified',
			headerName: t('Modified By'),
			flex: 1,
			minWidth: 150,
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		{
			field: 'Description',
			headerName: t('Description'),
			description: t('Description'),
			minWidth: 200,
			flex: 1,
			cellClassName: ({ row, value }) => {
				return row.MarkDescription === '1' ? 'auditing-value-changed' : ''
			},
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		{
			field: 'StatusIdName',
			headerName: t('Status'),
			description: t('Status'),
			flex: 1,
			minWidth: 200,
			cellClassName: ({ row, value }) => {
				return row.MarkStatusId === '1' ? 'auditing-value-changed' : ''
			}
		},
		{
			field: 'PriorityIdName',
			headerName: t('Priority'),
			description: t('Priority'),
			flex: 1,
			minWidth: 110,
			cellClassName: ({ row, value }) => {
				return row.MarkPriorityId === '1' ? 'auditing-value-changed' : ''
			}
		},
		{
			field: 'TypeIdName',
			headerName: t('Dimension'),
			description: t('Dimension'),
			flex: 1,
			minWidth: 110,
			cellClassName: ({ row, value }) => {
				return row.MarkTypeId === '1' ? 'auditing-value-changed' : ''
			}
		},
		{
			field: 'KindIdName',
			headerName: t('Issue Type'),
			description: t('Issue Type'),
			flex: 1,
			minWidth: 120,
			cellClassName: ({ row, value }) => {
				return row.MarkKindId === '1' ? 'auditing-value-changed' : ''
			}
		},
		{
			field: 'AssigneeIdName',
			headerName: t('Subcontractor'),
			description: t('Subcontractor'),
			flex: 1,
			minWidth: 150,
			cellClassName: ({ row, value }) => {
				return row.MarkAssigneeId === '1' ? 'auditing-value-changed' : ''
			},
			renderCell: (params) => {
				return <TruncateDescription value={params.value}/>
			},
		},
		{
			field: 'TargetedStartDt',
			headerName: t('Targeted Start'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			cellClassName: ({ row, value }) => {
				return row.MarkTargetedStartDt === '1' ? 'auditing-value-changed' : ''
			},
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat}`) : null)
			}
		},
		{
			field: 'TargetedStartTm',
			headerName: t('Targeted Start Time'),
			description: t('Targeted Start Time'),
			flex: 1,
			minWidth: 150,
			align: 'center',
			aggregable: false,
			cellClassName: ({ row, value }) => {
				return row.MarkTargetedStartTm === '1' ? 'auditing-value-changed' : ''
			},
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value, 'HH:mm:ss').format(`${timeFormat}`) : null)
			}
		},
		{
			field: 'ActualVisitDt',
			headerName: t('Actual Visit Date'),
			description: t('Actual Visit Date'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat}`) : null)
			},
			cellClassName: ({ row, value }) => {
				return row.MarkTargetedEndDt === '1' ? 'auditing-value-changed' : ''
			}
		},
		{
			field: 'ActualVisitTm',
			headerName: t('Actual Visit Time'),
			description: t('Actual Visit Time'),
			flex: 1,
			minWidth: 150,
			align: 'center',
			aggregable: false,
			cellClassName: ({ row, value }) => {
				return row.MarkTargetedEndTm === '1' ? 'auditing-value-changed' : ''
			},
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value, 'HH:mm:ss').format(`${timeFormat}`) : null)
			}
		},
		{
			field: 'TargetedEndDt',
			headerName: t('Targeted Expiry'),
			description: t('Targeted Expiry'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat}`) : null)
			},
			cellClassName: ({ row, value }) => {
				return row.MarkTargetedEndDt === '1' ? 'auditing-value-changed' : ''
			}
		},
		{
			field: 'TargetedEndTm',
			headerName: t('Targeted Expiry Time'),
			description: t('Targeted Expiry Time'),
			flex: 1,
			minWidth: 150,
			align: 'center',
			aggregable: false,
			cellClassName: ({ row, value }) => {
				return row.MarkTargetedEndTm === '1' ? 'auditing-value-changed' : ''
			},
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value, 'HH:mm:ss').format(`${timeFormat}`) : null)
			}
		},
		{
			field: 'ActualStartDt',
			headerName: t('Actual Start'),
			description: t('Actual Start'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			cellClassName: ({ row, value }) => {
				return row.MarkActualStartDt === '1' ? 'auditing-value-changed' : ''
			},
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
			}
		},
		{
			field: 'ActualEndDt',
			headerName: t('Actual End'),
			description: t('Actual End'),
			flex: 1,
			minWidth: 150,
			type: 'date',
			align: 'center',
			aggregable: false,
			cellClassName: ({ row, value }) => {
				return row.MarkActualEndDt === '1' ? 'auditing-value-changed' : ''
			},
			valueFormatter: (value, row, column, apiRef) => {
				return (value ? dayjs(value).format(`${dateFormat} ${timeFormat}`) : null)
			}
		},
	], [t, dateFormat])
	return (
		<DataGridWrapper height={'auto'}>
			<DataGridPremium
				loading={loading}
				columns={columns}
				initialState={{
					density: "compact",
					pagination: {
						paginationModel: { pageSize: 10 },
					},
					sorting: {
						sortModel: [{ field: 'ModifiedAt', sort: 'desc' }],
					},
				}}
				rows={entityHistory}
				slots={{
					pagination: CustomPagination,
				}}
				localeText={muiGridLocales(language)}
				pagination
				pageSizeOptions={[5, 10, 15, 20]}
			/>
		</DataGridWrapper>
	);
};

export default IssueAuditingHistory